"use client";

import { useEffect } from "react";
import { isDeploymentUpdateAvailable } from "@/lib/deployment-version";

const DEPLOYMENT_CHECK_INTERVAL_MS = 60_000;

export function DeploymentVersionWatcher({
  loadedDeploymentId,
}: {
  loadedDeploymentId?: string;
}) {
  useEffect(() => {
    if (!loadedDeploymentId) return;

    const controller = new AbortController();
    let checking = false;
    let reloading = false;

    const checkForUpdate = async () => {
      if (checking || reloading || navigator.onLine === false) return;
      checking = true;

      try {
        if (
          await isDeploymentUpdateAvailable(loadedDeploymentId, {
            signal: controller.signal,
          })
        ) {
          reloading = true;
          window.location.reload();
        }
      } catch {
        // A transient version check must never interrupt portal usage.
      } finally {
        checking = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkForUpdate();
      }
    };
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) void checkForUpdate();
    };
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        void checkForUpdate();
      }
    }, DEPLOYMENT_CHECK_INTERVAL_MS);

    void checkForUpdate();
    window.addEventListener("focus", checkForUpdate);
    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      controller.abort();
      window.clearInterval(intervalId);
      window.removeEventListener("focus", checkForUpdate);
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadedDeploymentId]);

  return null;
}
